import * as THREE from "three";

/**
 * Create a rectangular shape segment for wall geometry.
 *
 * This function creates 4-point rectangular shapes for wall segments.
 * It's primarily used for inner fill shapes and endcaps.
 *
 * Note: For edge connection outlines, a different system is used:
 * - Outline segments are calculated in src/utils/outlineSegmentation.ts
 * - These are rendered as 2-point line segments via WallOutlineSegment.tsx
 * - The createLineShape function in createShapes.ts is used for outline shape data
 *
 * @param wallStart - Starting point of the wall
 * @param startPosition - Position along the wall where this segment starts
 * @param endPosition - Position along the wall where this segment ends
 * @param wallDirection - Normalized direction vector of the wall
 * @param perpendicular - Perpendicular vector to the wall direction
 * @param lineWeight - Thickness of the outline line
 * @param wallWidth - Width of the wall
 * @param shapeType - Type of shape ("inner", "left", "right", "endCaps")
 * @returns THREE.Shape - A 4-point rectangular shape
 */
export const createWallSegment = (
  wallStart: THREE.Vector2,
  startPosition: number,
  endPosition: number,
  wallDirection: THREE.Vector2,
  perpendicular: THREE.Vector2,
  lineWeight: number,
  wallWidth: number,
  shapeType: string,
): THREE.Shape => {
  const shape = new THREE.Shape();
  const segmentStart = wallStart
    .clone()
    .add(wallDirection.clone().multiplyScalar(startPosition));
  const segmentEnd = wallStart
    .clone()
    .add(wallDirection.clone().multiplyScalar(endPosition));
  const shiftDistance = wallWidth / 2;

  if (shapeType === "left") {
    shape.moveTo(
      segmentStart.x - perpendicular.x * shiftDistance,
      segmentStart.y - perpendicular.y * shiftDistance,
    );
    shape.lineTo(
      segmentEnd.x - perpendicular.x * shiftDistance,
      segmentEnd.y - perpendicular.y * shiftDistance,
    );
    shape.lineTo(
      segmentEnd.x - perpendicular.x * (shiftDistance + lineWeight),
      segmentEnd.y - perpendicular.y * (shiftDistance + lineWeight),
    );
    shape.lineTo(
      segmentStart.x - perpendicular.x * (shiftDistance + lineWeight),
      segmentStart.y - perpendicular.y * (shiftDistance + lineWeight),
    );
  } else if (shapeType === "right") {
    shape.moveTo(
      segmentStart.x + perpendicular.x * shiftDistance,
      segmentStart.y + perpendicular.y * shiftDistance,
    );
    shape.lineTo(
      segmentEnd.x + perpendicular.x * shiftDistance,
      segmentEnd.y + perpendicular.y * shiftDistance,
    );
    shape.lineTo(
      segmentEnd.x + perpendicular.x * (shiftDistance + lineWeight),
      segmentEnd.y + perpendicular.y * (shiftDistance + lineWeight),
    );
    shape.lineTo(
      segmentStart.x + perpendicular.x * (shiftDistance + lineWeight),
      segmentStart.y + perpendicular.y * (shiftDistance + lineWeight),
    );
  } else {
    shape.moveTo(
      segmentStart.x + perpendicular.x * shiftDistance,
      segmentStart.y + perpendicular.y * shiftDistance,
    );
    shape.lineTo(
      segmentEnd.x + perpendicular.x * shiftDistance,
      segmentEnd.y + perpendicular.y * shiftDistance,
    );
    shape.lineTo(
      segmentEnd.x - perpendicular.x * shiftDistance,
      segmentEnd.y - perpendicular.y * shiftDistance,
    );
    shape.lineTo(
      segmentStart.x - perpendicular.x * shiftDistance,
      segmentStart.y - perpendicular.y * shiftDistance,
    );
  }
  shape.closePath();

  return shape;
};
